import { FunnelIcon, TrashIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { components, OptionProps } from 'react-select';

import { CompactIconButton } from '@/core/buttons/IconButton';
import { WindowedSelect } from '@/form/select';
import { translate } from '@/i18n';
import { useModal } from '@/modal/actions';
import { useNotify } from '@/store/notify';

import {
  clearAllFilters,
  selectSavedFilter,
  setFilter,
  setSavedFilters,
} from './actions';
import {
  selectSelectedSavedFilter,
  selectTableSavedFilters,
} from './selectors';
import { TableFilterService, TableFiltersGroup } from './TableFilterService';
import { TableProps } from './types';
import { getSavedFiltersKey } from './utils';

import './SavedFilterSelect.scss';

const Control = (props) => (
  <div className="d-flex align-items-center gap-2">
    <components.Control
      {...props}
      className={classNames('flex-grow-1', props.className)}
    >
      <span className="svg-icon svg-icon-2 svg-icon-gray-700 ms-3">
        <FunnelIcon weight="bold" />
      </span>
      {props.children}
    </components.Control>
    {Boolean(props.getValue()[0]) && (
      <CompactIconButton
        iconNode={<TrashIcon weight="bold" />}
        tooltip={translate('Delete filter')}
        onClick={(e) => props.remove(e, props.getValue()[0])}
        variant="text-danger"
        className="me-3"
      />
    )}
  </div>
);

const ListOption: FC<OptionProps & { remove }> = (props) => (
  <components.Option {...props}>
    <div className="d-flex justify-content-between align-items-center">
      {props.children}
      <CompactIconButton
        iconNode={<TrashIcon weight="bold" />}
        tooltip={translate('Delete filter')}
        onClick={(e) => props.remove(e, props.getValue()[0])}
        variant="text-danger"
        className="btn-remove"
      />
    </div>
  </components.Option>
);

interface SavedFilterSelectProps {
  table: string;
  formId: string;
  filterPosition?: TableProps['filterPosition'];
  onSelect?(): void;
}

export const SavedFilterSelect = ({
  table,
  formId,
  filterPosition,
  onSelect,
}: SavedFilterSelectProps) => {
  const { confirm } = useModal();
  const dispatch = useDispatch();
  const { showErrorResponse } = useNotify();

  const key = useMemo(() => getSavedFiltersKey(table, formId), [table, formId]);

  useEffect(() => {
    dispatch(setSavedFilters(table, TableFilterService.list(key).reverse()));
  }, [table, key, dispatch]);

  const list = useSelector((state: any) =>
    selectTableSavedFilters(state, table),
  );
  const selected = useSelector((state: any) =>
    selectSelectedSavedFilter(state, table),
  );

  const setSelected = useCallback(
    (value: TableFiltersGroup) => {
      const deselect = selected && value?.id === selected.id;
      dispatch(clearAllFilters(table));
      if (value && !deselect) {
        Object.entries(value.values).forEach(([name, val]) => {
          dispatch(
            setFilter(table, {
              name,
              value: val,
              label: null,
              component: null,
            }),
          );
        });
      }
      dispatch(setSavedFilters(table, TableFilterService.list(key).reverse()));
      if (!deselect && value) {
        dispatch(selectSavedFilter(table, value));
        if (onSelect) onSelect();
      } else {
        dispatch(selectSavedFilter(table, null));
      }
    },
    [table, key, onSelect, selected, dispatch],
  );

  const remove = useCallback(
    async (e, item) => {
      e.stopPropagation();
      try {
        await confirm(
          translate('Delete filter'),
          translate(
            'Are you sure you want to delete this filter? This action cannot be undone.',
          ),
          { forDeletion: true },
        );
      } catch {
        return;
      }
      try {
        TableFilterService.remove(key, item);
        setSelected(null);
        if (onSelect) onSelect();
      } catch (error) {
        showErrorResponse(error, translate('Unable to remove the filter.'));
      }
    },
    [setSelected, key, onSelect, confirm, showErrorResponse],
  );

  return (
    <div
      className={
        filterPosition === 'menu' ? 'saved-filters' : 'saved-filters mb-7'
      }
    >
      <WindowedSelect
        value={selected}
        onChange={setSelected}
        placeholder={translate('Select saved filter')}
        getOptionLabel={(option) => option.title}
        getOptionValue={(option) => option.id}
        options={list || []}
        isClearable={true}
        noOptionsMessage={() => translate('No saved filter')}
        {...(filterPosition === 'menu' ? { variant: 'tableFilter' } : {})}
        components={
          filterPosition === 'menu'
            ? {
                Option: (props) => <ListOption {...props} remove={remove} />,
              }
            : {
                Control: (props) => <Control {...props} remove={remove} />,
              }
        }
      />
    </div>
  );
};
